// Popup temporaire Gmail / Yahoo : le temps de revenir en boîte de réception.
// Passer à false pour l'éteindre. Les compteurs du dashboard restent.
export const INBOX_PROMPT_ENABLED = true;

export const INBOX_PROMPT_MAX_DISMISSALS = 3;

export function mailHost(email) {
	const domain = `${email || ''}`.toLowerCase().trim().split('@')[1] || '';
	if(domain === 'gmail.com' || domain === 'googlemail.com') {
		return 'gmail';
	}
	if(
		domain === 'ymail.com'
		|| domain === 'rocketmail.com'
		|| domain === 'yahoo.com'
		|| domain.startsWith('yahoo.')
	) {
		return 'yahoo';
	}
	return null;
}

export function brevoMessageId(result) {
	if(!result || result.skipped) {
		return '';
	}
	const body = result.body && typeof result.body === 'object' ? result.body : result;
	const raw = body.messageId || (Array.isArray(body.messageIds) ? body.messageIds[0] : '');
	return `${raw || ''}`.replace(/[<>]/g, '').trim();
}

export function canArmInboxPrompt(prompt) {
	if(!INBOX_PROMPT_ENABLED) {
		return false;
	}
	if(prompt && prompt.clickedAt) {
		return false;
	}
	if(prompt && (prompt.dismissals || 0) >= INBOX_PROMPT_MAX_DISMISSALS) {
		return false;
	}
	if(prompt && (prompt.shows || 0) >= INBOX_PROMPT_MAX_DISMISSALS) {
		return false;
	}
	return true;
}

export function buildInboxPromptPending(prompt, fields) {
	if(!canArmInboxPrompt(prompt) || !fields || !fields.messageId || !fields.provider) {
		return null;
	}
	return {
		...(prompt || {}),
		pending: {
			context: fields.context,
			messageId: fields.messageId,
			provider: fields.provider,
			subject: fields.subject || '',
			shown: false,
			at: fields.at,
		},
	};
}

export function inboxPromptPending(user, context) {
	if(!INBOX_PROMPT_ENABLED || !user || (user.role && user.role !== 'customer')) {
		return null;
	}
	const host = mailHost(user.email);
	if(!host) {
		return null;
	}
	const prompt = (user.preferences && user.preferences.inboxPrompt) || {};
	if(
		prompt.clickedAt
		|| (prompt.dismissals || 0) >= INBOX_PROMPT_MAX_DISMISSALS
		|| (prompt.shows || 0) >= INBOX_PROMPT_MAX_DISMISSALS
	) {
		return null;
	}
	const pending = prompt.pending;
	if(!pending || pending.shown || pending.context !== context || !pending.messageId) {
		return null;
	}
	if(pending.provider !== host) {
		return null;
	}
	return pending;
}

export function applyInboxPromptAction(prompt, action, context, now = new Date().toISOString()) {
	const current = {
		dismissals: 0,
		...(prompt || {}),
	};
	const pending = current.pending;
	if(!pending || pending.context !== context || !pending.messageId) {
		return { prompt: current, stat: null, changed: false };
	}
	if(action === 'shown') {
		if(pending.shown || current.clickedAt) {
			return { prompt: current, stat: null, changed: false };
		}
		return {
			prompt: {
				...current,
				shows: (current.shows || 0) + 1,
				pending: { ...pending, shown: true },
			},
			stat: 'shown',
			changed: true,
		};
	}
	if(action === 'dismiss') {
		if(current.clickedAt) {
			return { prompt: current, stat: null, changed: false };
		}
		return {
			prompt: {
				...current,
				pending: null,
				dismissals: (current.dismissals || 0) + 1,
			},
			stat: null,
			changed: true,
		};
	}
	if(action === 'click') {
		if(current.clickedAt) {
			return { prompt: current, stat: null, changed: false };
		}
		return {
			prompt: {
				...current,
				pending: null,
				clickedAt: now,
			},
			stat: 'clicked',
			changed: true,
		};
	}
	return { prompt: current, stat: null, changed: false };
}

export function mailboxUrl(email, pending) {
	if(!pending || !pending.messageId) {
		return '';
	}
	if(pending.provider === 'gmail') {
		const id = encodeURIComponent(pending.messageId);
		const auth = encodeURIComponent(email || '');
		return `https://mail.google.com/mail/u/0/?authuser=${auth}#search/in%3Aanywhere+rfc822msgid%3A${id}`;
	}
	if(pending.provider === 'yahoo') {
		return `https://mail.yahoo.com/d/search/keyword=${encodeURIComponent(pending.subject || '')}`;
	}
	return '';
}

// L'app Gmail ne sait pas ouvrir ce mail précis. Sur ordinateur, le site le
// retrouve (même dans les indésirables) et la session du navigateur est déjà là.
// Sur téléphone, l'app est connectée et le navigateur non : on ouvre l'app.
const GMAIL_IOS_URL = 'googlegmail:///';
const NATIVE_MAILBOX_FALLBACK_MS = 2000;
const NATIVE_MAILBOX_FALLBACK_GRACE_MS = 800;

export function mailboxPlatform(userAgent, maxTouchPoints = 0) {
	const ua = `${userAgent || ''}`;
	if(/Android/i.test(ua)) {
		return 'android';
	}
	if(/iPhone|iPad|iPod/i.test(ua)) {
		return 'ios';
	}
	// iPadOS se présente comme un Mac, mais avec un écran tactile.
	if(/Macintosh/i.test(ua) && maxTouchPoints > 1) {
		return 'ios';
	}
	return 'desktop';
}

export function nativeMailboxUrl(pending, platform, webUrl) {
	if(!pending || pending.provider !== 'gmail' || !webUrl) {
		return '';
	}
	if(platform === 'ios') {
		return GMAIL_IOS_URL;
	}
	if(platform === 'android') {
		const fallback = encodeURIComponent(webUrl);
		return `intent://#Intent;scheme=googlegmail;package=com.google.android.gm;S.browser_fallback_url=${fallback};end`;
	}
	return '';
}

function browserMailboxEnv() {
	return {
		userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
		maxTouchPoints: typeof navigator === 'undefined' ? 0 : (navigator.maxTouchPoints || 0),
		isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
		now: () => Date.now(),
		openWeb(url) {
			window.open(url, '_blank', 'noopener,noreferrer');
		},
		openApp(url) {
			window.location.href = url;
		},
		openFallback(url) {
			window.location.href = url;
		},
		later(fn, ms) {
			return window.setTimeout(fn, ms);
		},
	};
}

// Sur iPhone, si l'app ne s'ouvre pas, la page reste visible et le minuteur
// part à l'heure : on charge alors le site. Si l'app a pris la main, la page
// est cachée ou le minuteur ne reprend qu'au retour, bien plus tard.
export function scheduleMailboxFallback(webUrl, env = browserMailboxEnv()) {
	if(!webUrl) {
		return;
	}
	const start = env.now();
	env.later(() => {
		const stillHere = !env.isHidden()
			&& (env.now() - start) < NATIVE_MAILBOX_FALLBACK_MS + NATIVE_MAILBOX_FALLBACK_GRACE_MS;
		if(stillHere) {
			env.openFallback(webUrl);
		}
	}, NATIVE_MAILBOX_FALLBACK_MS);
}

export function openMailbox(email, pending, env = browserMailboxEnv()) {
	const webUrl = mailboxUrl(email, pending);
	if(!webUrl) {
		return;
	}
	const platform = mailboxPlatform(env.userAgent, env.maxTouchPoints);
	const appUrl = nativeMailboxUrl(pending, platform, webUrl);
	if(!appUrl) {
		env.openWeb(webUrl);
		return;
	}
	env.openApp(appUrl);
	// Android : le lien intent charge le site lui-même si l'app est absente.
	// Un second minuteur couperait la demande « Ouvrir avec Gmail ».
	if(platform === 'ios') {
		scheduleMailboxFallback(webUrl, env);
	}
}
